
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import {
  listHeroSliderBanners,
  updateHeroSliderBanner,
  uploadHeroSliderAsset,
} from "../admin/api/heroSliderApi";
import HeroBannersManager from "../admin/pages/HeroBannersManager";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const toAbsoluteUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^data:/i.test(raw) || /^blob:/i.test(raw)) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/landing/") || raw.startsWith("landing/")) {
    return raw.startsWith("/") ? raw : `/${raw}`;
  }
  const base = API_BASE.replace(/\/$/, "");
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${path}`;
};

const isYoutubeUrl = (value) => /youtube\.com|youtu\.be/i.test(String(value || ""));

const toYoutubeEmbedUrl = (value) => {
  try {
    const url = new URL(String(value || ""));
    if (url.hostname.includes("youtu.be")) {
      const id = url.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : value;
    }
    const id = url.searchParams.get("v");
    if (id) return `https://www.youtube.com/embed/${id}`;
  } catch (_) {
    // ignore parse errors
  }
  return value;
};

const DEFAULT_SPLIT3_ITEMS = [
  { position: "left", label: "Luxury Villas", title: "", description: "", image_url: "", is_active: true },
  { position: "middle", label: "Packages", title: "", description: "", image_url: "", is_active: true },
  { position: "right", label: "Cab Services", title: "", description: "", image_url: "", is_active: true },
];

const DEFAULT_SPLIT2_ITEMS = [
  { position: "left", label: "Left Panel", overlay_text: "", image_url: "", video_url: "", is_active: true },
  { position: "right", label: "Right Panel", overlay_text: "", image_url: "", video_url: "", is_active: true },
];

const splitPositions = {
  split3: ["left", "middle", "right"],
  split2: ["left", "right"],
};

const splitLabels = {
  split3: {
    left: "Luxury Villas",
    middle: "Packages",
    right: "Cab Services",
  },
  split2: {
    left: "Left Panel",
    right: "Right Panel",
  },
};

const resolveSplitItems = (items, type) => {
  const positions = splitPositions[type] || [];
  const labelMap = splitLabels[type] || {};
  const byPosition = new Map();
  (Array.isArray(items) ? items : []).forEach((item, index) => {
    const position = (item?.position || positions[index] || `item-${index + 1}`).toLowerCase();
    if (!position) return;
    byPosition.set(position, item);
  });

  const fallback =
    type === "split3" ? DEFAULT_SPLIT3_ITEMS : type === "split2" ? DEFAULT_SPLIT2_ITEMS : [];

  return positions.map((position, index) => {
    const raw = byPosition.get(position) || fallback[index] || {};
    const label = raw.label || labelMap[position] || raw.title || "";
    const overlayText = raw.overlay_text || raw.overlayText || raw.title || label || "";
    return {
      position,
      label,
      title: raw.title || "",
      description: raw.description || raw.subtitle || "",
      overlay_text: overlayText,
      image_url: raw.image_url || raw.imageUrl || "",
      video_url: raw.video_url || raw.videoUrl || "",
      is_active: raw.is_active !== false,
    };
  });
};

const buildFullForm = (banner) => ({
  id: banner?.id || "",
  banner_type: banner?.banner_type || "full",
  banner_order: Number(banner?.banner_order ?? 1),
  image_url: banner?.image_url || "",
  title: banner?.title || "",
  subtitle: banner?.subtitle || "",
  cta_text: banner?.cta_text || "",
  cta_link: banner?.cta_link || "",
  is_active: banner?.is_active !== false,
});

const buildSplitForm = (banner, type, order) => ({
  id: banner?.id || "",
  banner_type: banner?.banner_type || type,
  banner_order: Number(banner?.banner_order ?? order),
  split_items: resolveSplitItems(banner?.split_items, type),
  is_active: banner?.is_active !== false,
});

export default function ContentHeroBannerPage({ token }) {
  const [heroTab, setHeroTab] = useState("banner1");
  const [heroLoading, setHeroLoading] = useState(false);
  const [heroError, setHeroError] = useState("");
  const [heroSuccess, setHeroSuccess] = useState("");
  const [legacyMode, setLegacyMode] = useState(false);
  const [heroSaving, setHeroSaving] = useState({
    full: false,
    split3: false,
    split2: false,
  });
  const [uploading, setUploading] = useState({});
  const [fullForm, setFullForm] = useState(buildFullForm());
  const [split3Form, setSplit3Form] = useState(buildSplitForm(null, "split3", 2));
  const [split2Form, setSplit2Form] = useState(buildSplitForm(null, "split2", 3));

  const fullPreviewUrl = useMemo(() => toAbsoluteUrl(fullForm.image_url), [fullForm.image_url]);

  const loadHeroSlider = async () => {
    setHeroLoading(true);
    setHeroError("");
    try {
      const items = await listHeroSliderBanners(token);
      const list = Array.isArray(items) ? items : [];
      const normalizedTypes = list
        .map((item) => String(item?.banner_type || item?.bannerType || "").toLowerCase())
        .filter(Boolean);
      const supportsSplit =
        normalizedTypes.includes("full") ||
        normalizedTypes.includes("split3") ||
        normalizedTypes.includes("split2");
      const looksLegacy = list.some((item) => item?.type || item?.heading || item?.subheading);
      if (!supportsSplit && looksLegacy) {
        setLegacyMode(true);
        return;
      }
      setLegacyMode(false);
      const full = list.find((item) => item.banner_type === "full");
      const split3 = list.find((item) => item.banner_type === "split3");
      const split2 = list.find((item) => item.banner_type === "split2");

      setFullForm(buildFullForm(full));
      setSplit3Form(buildSplitForm(split3, "split3", 2));
      setSplit2Form(buildSplitForm(split2, "split2", 3));
    } catch (err) {
      setLegacyMode(false);
      setHeroError(err.message || "Unable to load hero slider banners.");
    } finally {
      setHeroLoading(false);
    }
  };

  useEffect(() => {
    loadHeroSlider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const updateSplitItem = (type, index, patch) => {
    if (type === "split3") {
      setSplit3Form((prev) => {
        const items = [...prev.split_items];
        items[index] = { ...items[index], ...patch };
        return { ...prev, split_items: items };
      });
      return;
    }
    setSplit2Form((prev) => {
      const items = [...prev.split_items];
      items[index] = { ...items[index], ...patch };
      return { ...prev, split_items: items };
    });
  };

  const uploadFullImage = async (file) => {
    if (!file || !fullForm.id) return;
    const key = "full";
    setUploading((prev) => ({ ...prev, [key]: true }));
    setHeroError("");
    try {
      const url = await uploadHeroSliderAsset(fullForm.id, file, null, token);
      if (!url) throw new Error("Upload failed.");
      setFullForm((prev) => ({ ...prev, image_url: url }));
    } catch (err) {
      setHeroError(err.message || "Unable to upload image.");
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const uploadSplitFile = async (type, index, file) => {
    if (!file) return;
    const key = `${type}-${index}`;
    const bannerId = type === "split3" ? split3Form.id : split2Form.id;
    if (!bannerId) {
      setHeroError("Banner is missing. Run the migration seed first.");
      return;
    }
    setUploading((prev) => ({ ...prev, [key]: true }));
    setHeroError("");
    try {
      const url = await uploadHeroSliderAsset(bannerId, file, index, token);
      if (!url) throw new Error("Upload failed.");
      const isVideo =
        String(file.type || "").toLowerCase().startsWith("video/") ||
        /\.mp4$|\.webm$/i.test(url);
      updateSplitItem(type, index, isVideo ? { video_url: url } : { image_url: url });
    } catch (err) {
      setHeroError(err.message || "Unable to upload file.");
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const saveFullBanner = async () => {
    if (!fullForm.id) {
      setHeroError("Banner 1 is missing. Run the migration seed first.");
      return;
    }
    setHeroSaving((prev) => ({ ...prev, full: true }));
    setHeroError("");
    setHeroSuccess("");
    try {
      const payload = {
        banner_type: fullForm.banner_type,
        banner_order: fullForm.banner_order,
        image_url: fullForm.image_url,
        title: fullForm.title,
        subtitle: fullForm.subtitle,
        cta_text: fullForm.cta_text,
        cta_link: fullForm.cta_link,
        is_active: fullForm.is_active,
      };
      await updateHeroSliderBanner(fullForm.id, payload, token);
      await loadHeroSlider();
      setHeroSuccess("Banner 1 updated.");
    } catch (err) {
      setHeroError(err.message || "Unable to save Banner 1.");
    } finally {
      setHeroSaving((prev) => ({ ...prev, full: false }));
    }
  };

  const saveSplit3Banner = async () => {
    if (!split3Form.id) {
      setHeroError("Banner 2 is missing. Run the migration seed first.");
      return;
    }
    setHeroSaving((prev) => ({ ...prev, split3: true }));
    setHeroError("");
    setHeroSuccess("");
    try {
      const payload = {
        banner_type: split3Form.banner_type,
        banner_order: split3Form.banner_order,
        split_items: split3Form.split_items,
        is_active: split3Form.is_active,
      };
      await updateHeroSliderBanner(split3Form.id, payload, token);
      await loadHeroSlider();
      setHeroSuccess("Banner 2 updated.");
    } catch (err) {
      setHeroError(err.message || "Unable to save Banner 2.");
    } finally {
      setHeroSaving((prev) => ({ ...prev, split3: false }));
    }
  };

  const saveSplit2Banner = async () => {
    if (!split2Form.id) {
      setHeroError("Banner 3 is missing. Run the migration seed first.");
      return;
    }
    setHeroSaving((prev) => ({ ...prev, split2: true }));
    setHeroError("");
    setHeroSuccess("");
    try {
      const payload = {
        banner_type: split2Form.banner_type,
        banner_order: split2Form.banner_order,
        split_items: split2Form.split_items,
        is_active: split2Form.is_active,
      };
      await updateHeroSliderBanner(split2Form.id, payload, token);
      await loadHeroSlider();
      setHeroSuccess("Banner 3 updated.");
    } catch (err) {
      setHeroError(err.message || "Unable to save Banner 3.");
    } finally {
      setHeroSaving((prev) => ({ ...prev, split2: false }));
    }
  };

  const refreshAll = () => {
    loadHeroSlider();
  };

  const isRefreshing = heroLoading;

  if (legacyMode) {
    return (
      <div className="p-4 bg-white rounded shadow-sm">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
          <div>
            <h4 className="mb-0">Landing Hero Banner</h4>
            <small className="text-muted">
              This environment uses the legacy hero banner schema. Manage the hero banners below.
            </small>
          </div>
          <Button variant="outline-secondary" onClick={loadHeroSlider} disabled={heroLoading}>
            {heroLoading ? (
              <>
                <Spinner size="sm" className="me-2" /> Refresh
              </>
            ) : (
              "Refresh"
            )}
          </Button>
        </div>
        {heroError ? <Alert variant="danger">{heroError}</Alert> : null}
        <HeroBannersManager token={token} />
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h4 className="mb-0">Landing Hero Banner</h4>
          <small className="text-muted">
            Manage the landing hero slider banners.
          </small>
        </div>
        <Button variant="outline-secondary" onClick={refreshAll} disabled={isRefreshing}>
          {isRefreshing ? (
            <>
              <Spinner size="sm" className="me-2" /> Refresh
            </>
          ) : (
            "Refresh"
          )}
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h5 className="mb-0">Hero slider banners</h5>
              <small className="text-muted">
                Manage the 3 hero banner layouts in the landing slider.
              </small>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={loadHeroSlider}
              disabled={heroLoading}
            >
              {heroLoading ? (
                <>
                  <Spinner size="sm" className="me-2" /> Refresh
                </>
              ) : (
                "Refresh"
              )}
            </Button>
          </div>
          {heroError ? <Alert variant="danger">{heroError}</Alert> : null}
          {heroSuccess ? <Alert variant="success">{heroSuccess}</Alert> : null}

          <Tabs
            activeKey={heroTab}
            onSelect={(k) => setHeroTab(k || "banner1")}
            className="mb-3"
          >
            <Tab eventKey="banner1" title="Banner 1">
              <Row className="g-3">
                <Col lg={6}>
                  <Form.Label className="small text-muted">Banner image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => uploadFullImage(e.target.files?.[0] || null)}
                  />
                  {uploading.full ? (
                    <div className="text-muted small mt-2">
                      <Spinner size="sm" className="me-2" /> Uploading...
                    </div>
                  ) : null}
                  <Form.Text className="text-muted">Or paste an image URL</Form.Text>
                  <Form.Control
                    className="mt-2"
                    value={fullForm.image_url}
                    onChange={(e) => setFullForm((prev) => ({ ...prev, image_url: e.target.value }))}
                    placeholder="/uploads/... or https://..."
                  />
                  {fullPreviewUrl ? (
                    <img
                      src={fullPreviewUrl}
                      alt="Banner preview"
                      className="mt-3 w-100 rounded-3 border"
                      style={{ maxHeight: 220, objectFit: "cover" }}
                    />
                  ) : null}
                </Col>
                <Col lg={6}>
                  <Row className="g-2">
                    <Col md={12}>
                      <Form.Label className="small text-muted">Heading</Form.Label>
                      <Form.Control
                        value={fullForm.title}
                        onChange={(e) => setFullForm((prev) => ({ ...prev, title: e.target.value }))}
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label className="small text-muted">Subheading</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={fullForm.subtitle}
                        onChange={(e) =>
                          setFullForm((prev) => ({ ...prev, subtitle: e.target.value }))
                        }
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label className="small text-muted">CTA text</Form.Label>
                      <Form.Control
                        value={fullForm.cta_text}
                        onChange={(e) =>
                          setFullForm((prev) => ({ ...prev, cta_text: e.target.value }))
                        }
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label className="small text-muted">CTA link</Form.Label>
                      <Form.Control
                        value={fullForm.cta_link}
                        onChange={(e) =>
                          setFullForm((prev) => ({ ...prev, cta_link: e.target.value }))
                        }
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Check
                        type="switch"
                        label={fullForm.is_active ? "Active" : "Inactive"}
                        checked={fullForm.is_active}
                        onChange={(e) =>
                          setFullForm((prev) => ({ ...prev, is_active: e.target.checked }))
                        }
                      />
                    </Col>
                    <Col md={12}>
                      <Button variant="primary" onClick={saveFullBanner} disabled={heroSaving.full}>
                        {heroSaving.full ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Saving
                          </>
                        ) : (
                          "Save Banner 1"
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="banner2" title="Banner 2">
              <Row className="g-3">
                {split3Form.split_items.map((item, index) => {
                  const uploadKey = `split3-${index}`;
                  const label =
                    item.label ||
                    (item.position === "left"
                      ? "Luxury Villas"
                      : item.position === "middle"
                      ? "Packages"
                      : "Cab Services");
                  return (
                    <Col md={4} key={item.position || index}>
                      <Card className="border h-100">
                        <Card.Body>
                          <div className="fw-semibold mb-2">{label}</div>
                          <Form.Label className="small text-muted">Title</Form.Label>
                          <Form.Control
                            value={item.title}
                            onChange={(e) =>
                              updateSplitItem("split3", index, { title: e.target.value })
                            }
                          />
                          <Form.Label className="small text-muted mt-2">Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={item.description}
                            onChange={(e) =>
                              updateSplitItem("split3", index, { description: e.target.value })
                            }
                          />
                          <Form.Label className="small text-muted mt-2">Image</Form.Label>
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                              uploadSplitFile("split3", index, e.target.files?.[0])
                            }
                          />
                          {uploading[uploadKey] ? (
                            <div className="text-muted small mt-2">
                              <Spinner size="sm" className="me-2" /> Uploading...
                            </div>
                          ) : null}
                          <Form.Control
                            className="mt-2"
                            value={item.image_url}
                            onChange={(e) =>
                              updateSplitItem("split3", index, { image_url: e.target.value })
                            }
                            placeholder="/uploads/... or https://..."
                          />
                          {item.image_url ? (
                            <img
                              src={toAbsoluteUrl(item.image_url)}
                              alt="Service preview"
                              className="mt-2 w-100 rounded-3 border"
                              style={{ maxHeight: 160, objectFit: "cover" }}
                            />
                          ) : null}
                          <Form.Check
                            className="mt-2"
                            type="switch"
                            label={item.is_active ? "Active" : "Inactive"}
                            checked={item.is_active}
                            onChange={(e) =>
                              updateSplitItem("split3", index, { is_active: e.target.checked })
                            }
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
              <div className="mt-3">
                <Form.Check
                  type="switch"
                  label={split3Form.is_active ? "Banner Active" : "Banner Inactive"}
                  checked={split3Form.is_active}
                  onChange={(e) =>
                    setSplit3Form((prev) => ({ ...prev, is_active: e.target.checked }))
                  }
                />
              </div>
              <Button
                variant="primary"
                className="mt-3"
                onClick={saveSplit3Banner}
                disabled={heroSaving.split3}
              >
                {heroSaving.split3 ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Saving
                  </>
                ) : (
                  "Save Banner 2"
                )}
              </Button>
            </Tab>

            <Tab eventKey="banner3" title="Banner 3">
              <Row className="g-3">
                {split2Form.split_items.map((item, index) => {
                  const uploadKey = `split2-${index}`;
                  return (
                    <Col md={6} key={item.position || index}>
                      <Card className="border h-100">
                        <Card.Body>
                          <div className="fw-semibold mb-2">
                            {item.label || (item.position === "left" ? "Left Panel" : "Right Panel")}
                          </div>
                          <Form.Label className="small text-muted">Overlay label</Form.Label>
                          <Form.Control
                            value={item.overlay_text}
                            onChange={(e) =>
                              updateSplitItem("split2", index, { overlay_text: e.target.value })
                            }
                            placeholder="Overlay text"
                          />
                          <Form.Label className="small text-muted mt-2">Image</Form.Label>
                          <Form.Control
                            type="file"
                            accept="image/*,video/mp4,video/webm"
                            onChange={(e) =>
                              uploadSplitFile("split2", index, e.target.files?.[0])
                            }
                          />
                          {uploading[uploadKey] ? (
                            <div className="text-muted small mt-2">
                              <Spinner size="sm" className="me-2" /> Uploading...
                            </div>
                          ) : null}
                          <Form.Control
                            className="mt-2"
                            value={item.image_url}
                            onChange={(e) =>
                              updateSplitItem("split2", index, { image_url: e.target.value })
                            }
                            placeholder="/uploads/... or https://..."
                          />
                          <Form.Label className="small text-muted mt-2">
                            Video URL (optional)
                          </Form.Label>
                          <Form.Control
                            value={item.video_url}
                            onChange={(e) =>
                              updateSplitItem("split2", index, { video_url: e.target.value })
                            }
                            placeholder="https://..."
                          />
                          {item.video_url ? (
                            isYoutubeUrl(item.video_url) ? (
                              <div className="mt-2 ratio ratio-16x9 rounded-3 border overflow-hidden">
                                <iframe
                                  src={toYoutubeEmbedUrl(item.video_url)}
                                  title="Video preview"
                                  allow="autoplay; encrypted-media"
                                  allowFullScreen
                                />
                              </div>
                            ) : (
                              <video
                                className="mt-2 w-100 rounded-3 border"
                                style={{ maxHeight: 180, objectFit: "cover" }}
                                controls
                                muted
                              >
                                <source src={toAbsoluteUrl(item.video_url)} />
                              </video>
                            )
                          ) : item.image_url ? (
                            <img
                              src={toAbsoluteUrl(item.image_url)}
                              alt="Accommodation preview"
                              className="mt-2 w-100 rounded-3 border"
                              style={{ maxHeight: 180, objectFit: "cover" }}
                            />
                          ) : null}
                          <Form.Check
                            className="mt-2"
                            type="switch"
                            label={item.is_active ? "Active" : "Inactive"}
                            checked={item.is_active}
                            onChange={(e) =>
                              updateSplitItem("split2", index, { is_active: e.target.checked })
                            }
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
              <div className="mt-3">
                <Form.Check
                  type="switch"
                  label={split2Form.is_active ? "Banner Active" : "Banner Inactive"}
                  checked={split2Form.is_active}
                  onChange={(e) =>
                    setSplit2Form((prev) => ({ ...prev, is_active: e.target.checked }))
                  }
                />
              </div>
              <Button
                variant="primary"
                className="mt-3"
                onClick={saveSplit2Banner}
                disabled={heroSaving.split2}
              >
                {heroSaving.split2 ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Saving
                  </>
                ) : (
                  "Save Banner 3"
                )}
              </Button>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </div>
  );
}


